import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db, storage } from '../firebase/config';
import { 
    collection, 
    query, 
    orderBy, 
    onSnapshot, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    doc,
    getDoc,
    arrayUnion,
    arrayRemove,
    increment,
    serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Swal from 'sweetalert2';
import './Events.css';

const Events = () => {
    const { currentUser, userData, isAdmin } = useAuth();
    const { showToast } = useToast();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [attendees, setAttendees] = useState([]);
    const [showAttendees, setShowAttendees] = useState(false);
    const [formData, setFormData] = useState({
        eventName: '',
        eventType: '',
        startDate: '',
        endDate: '',
        location: '',
        description: '',
        contactPerson: '',
        contactPhone: '',
        recurrence: 'none',
        imageUrl: '',
        imageFile: null
    });
    const [imagePreview, setImagePreview] = useState('');

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = () => {
        const eventsQuery = query(
            collection(db, 'events'),
            orderBy('startDate', 'desc')
        );

        const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
            const eventsData = [];
            snapshot.forEach(doc => {
                eventsData.push({ id: doc.id, ...doc.data() });
            });
            setEvents(eventsData);
            setLoading(false);
        }, (error) => {
            console.error('Error loading events:', error);
            showToast('Error loading events', 'error');
            setLoading(false);
        });

        return unsubscribe;
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                showToast('Please select an image file', 'error');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                showToast('Image size must be less than 5MB', 'error');
                return;
            }

            setFormData(prev => ({ ...prev, imageFile: file }));
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadImage = async (file) => {
        try {
            const storageRef = ref(storage, `event-images/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading image:', error);
            throw new Error('Failed to upload image');
        }
    };

    const resetForm = () => {
        setFormData({
            eventName: '',
            eventType: '',
            startDate: '',
            endDate: '',
            location: '',
            description: '',
            contactPerson: '',
            contactPhone: '',
            recurrence: 'none',
            imageUrl: '',
            imageFile: null
        });
        setImagePreview('');
        setEditingEvent(null);
    };

    const validateForm = () => {
        if (!formData.eventName || !formData.eventType || !formData.startDate || !formData.endDate || 
            !formData.location || !formData.contactPerson || !formData.contactPhone) {
            showToast('Please fill in all required fields', 'error');
            return false;
        }

        if (new Date(formData.endDate) < new Date(formData.startDate)) {
            showToast('End date must be after start date', 'error');
            return false;
        }

        const phoneRegex = /^254[0-9]{9}$/;
        if (!phoneRegex.test(formData.contactPhone.replace(/\D/g, ''))) {
            showToast('Please enter a valid Kenyan phone number starting with 254 followed by 9 digits', 'error');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            let imageUrl = formData.imageUrl;

            if (formData.imageFile) {
                imageUrl = await uploadImage(formData.imageFile);
            }

            const eventData = {
                eventName: formData.eventName,
                eventType: formData.eventType,
                startDate: formData.startDate,
                endDate: formData.endDate,
                location: formData.location,
                description: formData.description,
                contactPerson: formData.contactPerson,
                contactPhone: formData.contactPhone,
                recurrence: formData.recurrence === 'none' ? null : formData.recurrence,
                imageUrl: imageUrl || '',
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid
            };

            if (editingEvent) {
                // Update existing event
                await updateDoc(doc(db, 'events', editingEvent.id), eventData);
                showToast('Event updated successfully!', 'success');
            } else {
                // Create new event
                eventData.createdAt = serverTimestamp();
                eventData.createdBy = currentUser.uid;
                eventData.viewCount = 0;
                eventData.attendees = [];

                await addDoc(collection(db, 'events'), eventData);
                showToast('Event created successfully!', 'success');
            }

            setShowForm(false);
            resetForm();
        } catch (error) {
            console.error('Error saving event:', error);
            showToast('Error saving event', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (event) => {
        setEditingEvent(event);
        setFormData({
            eventName: event.eventName || '',
            eventType: event.eventType || '',
            startDate: event.startDate || '',
            endDate: event.endDate || '',
            location: event.location || '',
            description: event.description || '',
            contactPerson: event.contactPerson || '',
            contactPhone: event.contactPhone || '',
            recurrence: event.recurrence || 'none',
            imageUrl: event.imageUrl || '',
            imageFile: null
        });
        setImagePreview(event.imageUrl || '');
        setShowForm(true);
    };

    const handleDelete = async (eventId) => {
        const result = await Swal.fire({
            title: 'Delete Event?',
            text: 'This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'events', eventId));
                showToast('Event deleted successfully!', 'success');
            } catch (error) {
                console.error('Error deleting event:', error);
                showToast('Error deleting event', 'error');
            }
        }
    };

    const handleRegister = async (eventId) => {
        try {
            const eventRef = doc(db, 'events', eventId);
            const eventDoc = await getDoc(eventRef);
            
            if (!eventDoc.exists()) {
                showToast('Event not found', 'error');
                return;
            }

            const event = eventDoc.data();
            
            // Check if already registered
            if (event.attendees?.some(a => a.userId === currentUser.uid)) {
                showToast('You are already registered for this event', 'error');
                return;
            }

            const attendeeData = {
                userId: currentUser.uid,
                fullName: userData?.fullName || 'Unknown',
                phone: userData?.phone || '',
                email: currentUser.email || '',
                registeredAt: serverTimestamp()
            };

            await updateDoc(eventRef, {
                attendees: arrayUnion(attendeeData)
            });

            showToast('Successfully registered for the event!', 'success');
        } catch (error) {
            console.error('Error registering for event:', error);
            showToast('Error registering for event', 'error');
        }
    };

    const handleUnregister = async (eventId) => {
        try {
            const eventRef = doc(db, 'events', eventId);
            const eventDoc = await getDoc(eventRef);
            
            if (!eventDoc.exists()) {
                showToast('Event not found', 'error');
                return;
            }

            const event = eventDoc.data();
            const attendee = event.attendees?.find(a => a.userId === currentUser.uid);

            if (!attendee) {
                showToast('You are not registered for this event', 'error');
                return;
            }

            await updateDoc(eventRef, {
                attendees: arrayRemove(attendee)
            });

            showToast('Registration cancelled', 'success');
        } catch (error) {
            console.error('Error unregistering from event:', error);
            showToast('Error cancelling registration', 'error');
        }
    };

    const loadAttendees = async (eventId) => {
        try {
            const eventDoc = await getDoc(doc(db, 'events', eventId));
            if (eventDoc.exists()) {
                setAttendees(eventDoc.data().attendees || []);
                setSelectedEventId(eventId);
                setShowAttendees(true);
            }
        } catch (error) {
            console.error('Error loading attendees:', error);
            showToast('Error loading attendees', 'error');
        }
    };

    const exportAttendees = () => {
        if (!selectedEventId || attendees.length === 0) return;

        const event = events.find(e => e.id === selectedEventId);
        const csvContent = [
            ['Name', 'Phone', 'Email', 'Registration Date'],
            ...attendees.map(a => [
                a.fullName,
                a.phone,
                a.email,
                new Date(a.registeredAt?.toDate?.() || a.registeredAt).toLocaleString()
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${event?.eventName || 'event'}_attendees.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const trackView = async (eventId) => {
        try {
            const eventRef = doc(db, 'events', eventId);
            await updateDoc(eventRef, {
                viewCount: increment(1)
            });
        } catch (error) {
            console.error('Error tracking view:', error);
        }
    };

    const getEventStatus = (event) => {
        const now = new Date();
        const start = new Date(event.startDate);
        const end = new Date(event.endDate);

        if (now > end) return { text: 'Completed', class: 'completed' };
        if (now >= start && now <= end) return { text: 'Active', class: 'active' };
        return { text: 'Planned', class: 'planned' };
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading && events.length === 0) {
        return (
            <div className="text-center" style={{ padding: '50px' }}>
                <i className="fas fa-spinner fa-spin fa-3x" style={{ color: '#1034A6' }}></i>
                <p style={{ marginTop: '20px' }}>Loading events...</p>
            </div>
        );
    }

    return (
        <div className="events-page">
            <div className="events-header">
                <div>
                    <h1 className="events-title">Ministry Events</h1>
                    <p className="events-subtitle">Join us in fellowship and worship</p>
                </div>
                {isAdmin && (
                    <button
                        className="events-add-btn"
                        onClick={() => {
                            resetForm();
                            setShowForm(!showForm);
                        }}
                    >
                        <i className="fas fa-plus"></i>
                        {showForm ? 'Cancel' : 'Add Event'}
                    </button>
                )}
            </div>

            {/* Event Form */}
            {showForm && isAdmin && (
                <div className="events-form-container">
                    <h2 className="events-form-title">
                        {editingEvent ? 'Edit Event' : 'Create New Event'}
                    </h2>
                    <form onSubmit={handleSubmit} className="events-form">
                        <div className="events-form-grid">
                            <div className="events-form-group">
                                <label htmlFor="eventName">Event Name *</label>
                                <input
                                    type="text"
                                    id="eventName"
                                    value={formData.eventName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="events-form-group">
                                <label htmlFor="eventType">Event Type *</label>
                                <select
                                    id="eventType"
                                    value={formData.eventType}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Type</option>
                                    <option value="worship">Worship Service</option>
                                    <option value="prayer">Prayer Meeting</option>
                                    <option value="bible">Bible Study</option>
                                    <option value="youth">Youth Event</option>
                                    <option value="community">Community Outreach</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="events-form-group">
                                <label htmlFor="startDate">Start Date & Time *</label>
                                <input
                                    type="datetime-local"
                                    id="startDate"
                                    value={formData.startDate}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="events-form-group">
                                <label htmlFor="endDate">End Date & Time *</label>
                                <input
                                    type="datetime-local"
                                    id="endDate"
                                    value={formData.endDate}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="events-form-group">
                                <label htmlFor="location">Location *</label>
                                <input
                                    type="text"
                                    id="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="events-form-group">
                                <label htmlFor="recurrence">Recurrence</label>
                                <select
                                    id="recurrence"
                                    value={formData.recurrence}
                                    onChange={handleInputChange}
                                >
                                    <option value="none">Does not repeat</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>

                            <div className="events-form-group full-width">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    rows="4"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>

                            <div className="events-form-group">
                                <label htmlFor="contactPerson">Contact Person *</label>
                                <input
                                    type="text"
                                    id="contactPerson"
                                    value={formData.contactPerson}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="events-form-group">
                                <label htmlFor="contactPhone">Contact Phone *</label>
                                <input
                                    type="tel"
                                    id="contactPhone"
                                    value={formData.contactPhone}
                                    onChange={handleInputChange}
                                    placeholder="254XXXXXXXXX"
                                    required
                                />
                            </div>

                            <div className="events-form-group">
                                <label htmlFor="imageFile">Upload Image</label>
                                <input
                                    type="file"
                                    id="imageFile"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                                {imagePreview && (
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="events-image-preview"
                                    />
                                )}
                            </div>

                            <div className="events-form-group">
                                <label htmlFor="imageUrl">Or Image URL</label>
                                <input
                                    type="url"
                                    id="imageUrl"
                                    value={formData.imageUrl}
                                    onChange={handleInputChange}
                                    placeholder="https://..."
                                />
                            </div>
                        </div>

                        <div className="events-form-actions">
                            <button
                                type="button"
                                className="events-btn-secondary"
                                onClick={() => {
                                    setShowForm(false);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="events-btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i>
                                        {editingEvent ? 'Update Event' : 'Create Event'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Events Grid */}
            <div className="events-grid">
                {events.length === 0 ? (
                    <div className="events-empty">
                        <i className="fas fa-calendar-alt"></i>
                        <h3>No Events Found</h3>
                        <p>Create your first event to get started</p>
                    </div>
                ) : (
                    events.map(event => {
                        const status = getEventStatus(event);
                        const isRegistered = event.attendees?.some(a => a.userId === currentUser.uid);

                        return (
                            <div
                                key={event.id}
                                className="events-card"
                                onClick={() => trackView(event.id)}
                            >
                                <div className="events-image-container">
                                    {event.imageUrl ? (
                                        <img
                                            src={event.imageUrl}
                                            alt={event.eventName}
                                            className="events-image"
                                        />
                                    ) : (
                                        <div className="events-no-image">
                                            <i className="fas fa-calendar-alt"></i>
                                        </div>
                                    )}
                                    <span className={`events-status-badge status-${status.class}`}>
                                        {status.text}
                                    </span>
                                    {event.recurrence && (
                                        <span className="events-recurring-badge">
                                            <i className="fas fa-sync-alt"></i> {event.recurrence}
                                        </span>
                                    )}
                                </div>

                                <div className="events-content">
                                    <h3 className="events-title">{event.eventName}</h3>

                                    <div className="events-details">
                                        <div className="events-detail">
                                            <i className="fas fa-calendar-alt"></i>
                                            <span>{formatDate(event.startDate)}</span>
                                        </div>
                                        <div className="events-detail">
                                            <i className="fas fa-clock"></i>
                                            <span>to {formatDate(event.endDate)}</span>
                                        </div>
                                        <div className="events-detail">
                                            <i className="fas fa-map-marker-alt"></i>
                                            <span>{event.location}</span>
                                        </div>
                                        <div className="events-detail">
                                            <i className="fas fa-tag"></i>
                                            <span className="events-type">{event.eventType}</span>
                                        </div>
                                    </div>

                                    {event.description && (
                                        <p className="events-description">{event.description}</p>
                                    )}

                                    <div className="events-contact">
                                        <div className="events-detail">
                                            <i className="fas fa-user"></i>
                                            <span>{event.contactPerson}</span>
                                        </div>
                                        <div className="events-detail">
                                            <i className="fas fa-phone"></i>
                                            <span>{event.contactPhone}</span>
                                        </div>
                                    </div>

                                    <div className="events-stats">
                                        <div className="events-stat">
                                            <i className="fas fa-eye"></i>
                                            <span>{event.viewCount || 0} views</span>
                                        </div>
                                        <div className="events-stat">
                                            <i className="fas fa-users"></i>
                                            <span>{event.attendees?.length || 0} attending</span>
                                        </div>
                                    </div>

                                    <div className="events-actions">
                                        {isRegistered ? (
                                            <button
                                                className="events-unregister-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleUnregister(event.id);
                                                }}
                                            >
                                                <i className="fas fa-times-circle"></i> Cancel
                                            </button>
                                        ) : (
                                            <button
                                                className="events-register-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRegister(event.id);
                                                }}
                                                disabled={status.class === 'completed'}
                                            >
                                                <i className="fas fa-check-circle"></i> Register
                                            </button>
                                        )}

                                        {isAdmin && (
                                            <>
                                                <button
                                                    className="events-attendees-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        loadAttendees(event.id);
                                                    }}
                                                >
                                                    <i className="fas fa-users"></i>
                                                </button>
                                                <button
                                                    className="events-edit-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(event);
                                                    }}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="events-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(event.id);
                                                    }}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Attendees Modal */}
            {showAttendees && (
                <div className="events-modal" onClick={() => setShowAttendees(false)}>
                    <div className="events-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="events-modal-header">
                            <h3 className="events-modal-title">Event Attendees</h3>
                            <button
                                className="events-modal-close"
                                onClick={() => setShowAttendees(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="events-modal-body">
                            {attendees.length === 0 ? (
                                <p className="text-center">No attendees yet</p>
                            ) : (
                                <div className="events-attendees-list">
                                    {attendees.map((attendee, index) => (
                                        <div key={index} className="events-attendee">
                                            <div className="events-attendee-avatar">
                                                {attendee.fullName.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="events-attendee-info">
                                                <div className="events-attendee-name">
                                                    {attendee.fullName}
                                                </div>
                                                <div className="events-attendee-contact">
                                                    {attendee.phone} • {attendee.email}
                                                </div>
                                                <div className="events-attendee-date">
                                                    Registered: {formatDate(attendee.registeredAt)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="events-modal-footer">
                            <button
                                className="events-btn-primary"
                                onClick={exportAttendees}
                                disabled={attendees.length === 0}
                            >
                                <i className="fas fa-download"></i> Export to CSV
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Events;
